<template>
  <div class="w-full">
    <div
      v-if="isLoading"
      class="d-flex flex-column align-center justify-center mb-4"
    >
      <GenericAnimation
        :animation-data="animationData"
        loop
        autoplay
        :width="100"
        :speed="0.5"
      />
      <span
        v-if="leadIds.length > 1"
        class="text-center"
      >
        {{ $t('leads.master.move-contacts.button.info-one') }}
      </span>
      <span
        v-else
        class="text-center"
      >
        {{ $t('leads.master.move-contacts.button.info-two') }}
      </span>
    </div>

    <v-tooltip
      location="bottom"
      :disabled="leadIds.length > 0"
    >
      <template v-slot:activator="{ props }">
        <div v-bind="props">
          <v-btn
            class="w-full primary my-2"
            size="small"
            :disabled="isLoading || leadIds.length === 0 || !selectedChildCompany"
            :loading="isLoading"
            @click="submit"
          >
            <v-icon
              size="small"
              class="mr-2"
            >
              mdi-rocket-launch
            </v-icon>

            {{ $t('leads.master.move-contacts.button.move-contacts') }}
          </v-btn>
        </div>
      </template>

      <span v-if="leadIds.length === 0">
        {{ $t('leads.master.move-contacts.select-contacts-warning') }}
      </span>
    </v-tooltip>
  </div>
</template>

<script setup lang="ts">
import { useApolloClient } from '@vue/apollo-composable'
import { onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import AnimationData from '@/assets/animations/email-plane.json'
import GenericAnimation from '@/components/GenericAnimation.vue'
import bus, { eventNames } from '@/lib/eventBus'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import MOVE_CONTACTS from '@/modules/master/queries/MoveContacts.gql'

const { t } = useI18n()

// --- Props ---
const props = defineProps({
  leadIds: {
    type: Array,
    default: () => [],
  },
  selectedChildCompany: {
    type: String,
    default: '',
  },
  contactFilter: {
    type: String,
    default: '',
  },
})

// --- Emits ---
const emit = defineEmits(['contactsMoved', 'isLoading'])

// --- Apollo Client ---
const { client } = useApolloClient()

// --- State ---
const isLoading = ref(false)
const animationData = ref(AnimationData)
let loadingTimeout = null

// --- Methods ---
function submit() {
  isLoading.value = true
  emit('isLoading', true)

  if (props.leadIds.length === 0 || !props.selectedChildCompany) {
    showSnackbarMessage('error', t('leads.master.move-contacts.select-contacts-error'))
    clearTimeout(loadingTimeout)
    isLoading.value = false
    emit('isLoading', false)
  }

  loadingTimeout = setTimeout(() => {
    moveContacts()
  }, 5000)
}

async function moveContacts() {
  try {
    await client.mutate({
      mutation: MOVE_CONTACTS,
      variables: {
        input: {
          leadIds: props.leadIds,
          targetCompanyId: props.selectedChildCompany,
        },
      },
    })

    bus.$emit(eventNames.TRIGGER_ANIMATION)
    showSnackbarMessage('success', t('leads.master.move-contacts.select-contacts-successful'))
    emit('contactsMoved')
  } catch (e) {
    if (e.graphQLErrors?.[0]?.message === 'ENTITY_ALREADY_EXISTS_ERROR') {
      showSnackbarMessage(
        'error',
        t('leads.master.move-contacts.select-contacts.email-exists-error')
      )
    } else {
      showSnackbarMessage(
        'error',
        t('leads.master.move-contacts.select-contacts.move-contacts-failed-error')
      )
    }
  } finally {
    isLoading.value = false
    emit('isLoading', false)
  }
}

onUnmounted(() => {
  if (loadingTimeout) {
    clearTimeout(loadingTimeout)
  }
})
</script>
